<template>
    <div
        class="vehicle-owner-info"
        :class="{
            'vehicle-owner-info--organization': owner.isOrganizationRepresentative,
        }"
        @click="$emit('click')"
    >
        <template v-if="full">
            <div class="vehicle-owner-info__box">
                <div class="vehicle-owner-info__header">
                    <OrganizationLogo v-if="owner.isOrganizationRepresentative" :user="owner" class="user-info__logo" />

                    <div class="vehicle-owner-info__header-left">
                        <div class="vehicle-owner-info__name">
                            {{ $t('pages.vehicle.owner_info_heading', { name: ownerDisplayName }) }}
                        </div>
                        <Ratings
                            v-if="$store.getters.isCountrySweden || vehicle.reviewCount > 0"
                            :value="vehicle.averageRating"
                            :number-of-votes="vehicle.reviewCount"
                            class="vehicle-owner-info__ratings"
                        />
                    </div>
                    <Avatar
                        v-if="!owner.isOrganizationRepresentative"
                        class="vehicle-owner-info__avatar"
                        :use-full-size-img="true"
                        :user="owner"
                    />
                </div>
                <UserVerificationsAndStats class="vehicle-owner-info__stats" :user="owner" :show-stats="true" />
            </div>
            <button
                v-if="shouldShowSendMessageButton"
                :disabled="!featureEnabled('renterAccess') || isImpersonating"
                class="button button--secondary-contrast button--small vehicle-owner-info__send-button"
                @click="$emit('send-message-click')"
            >
                {{ $t('pages.vehicle.send_message_button') }}
            </button>
        </template>
        <UserInfo
            v-else
            :user="owner"
            :show-meta="false"
            :show-ratings="true"
            :average-rating="vehicle.averageRating"
            :review-count="vehicle.reviewCount"
            @ratings-click="$emit('ratings-click')"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import UserInfo from '~/components/UserInfo.vue';
import UserVerificationsAndStats from '~/components/UserVerificationsAndStats.vue';
import Ratings from '~/components/Ratings.vue';
import Avatar from '~/components/Avatar.vue';
import OrganizationLogo from '~/components/OrganizationLogo.vue';

export default {
    components: { UserInfo, UserVerificationsAndStats, Ratings, Avatar, OrganizationLogo },
    props: {
        vehicle: {
            type: Object,
            default: () => {},
        },
        full: {
            type: Boolean,
            default: false,
        },
        showSendMessage: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters(['isLoggedIn', 'isRenter', 'user', 'isImpersonating']),
        shouldShowSendMessageButton() {
            if (!this.showSendMessage) {
                return false;
            }

            if (!this.isLoggedIn) {
                // Click on the button will open login modal
                return true;
            }

            if (!this.isRenter) {
                return false;
            }

            return true;
        },
        owner() {
            return this.vehicle.owner;
        },
        ownerDisplayName() {
            const { owner } = this;

            if (!owner.isOrganizationRepresentative || !owner.organizationName) {
                return owner.firstName;
            }

            return owner.organizationName;
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.vehicle-owner-info {
    .user-info {
        margin-bottom: 10px;
    }

    &__box {
        border-radius: $default-border-radius;
        background-color: $cream-white;
        padding: 20px;
        margin-bottom: 20px;

        @include lg {
            width: 475px;
        }
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__name {
        @include font-size(20px);
        font-weight: 700;
        line-height: 1.5em;
        text-align: left;
    }

    &__avatar {
        margin-left: 30px;

        .avatar {
            width: 80px;
            height: 80px;
        }

        @include lg {
            margin-right: 40px;
        }
    }

    &__stats {
        margin-top: 10px;
    }

    &__send-button {
        width: 100%;

        @include lg {
            width: auto;
        }
    }

    &--organization {
        .vehicle-owner-info__box {
            text-align: left;
        }

        .vehicle-owner-info__header {
            display: initial;
        }

        .vehicle-owner-info__stats {
            margin-top: 20px;
        }
    }
}

.info-item {
    &__icon.svg-icon {
        min-width: 24px;
        min-height: 24px;
        margin-right: 5px;
        color: $primary;
    }
}
</style>
